import { type FC } from "react";
import { Table } from "@root/shared/components/Table/legacy/Table";
import { DetailTableRow } from "@clientFrontend/features/publication/components/DetailTableRow";
import { MultiLineText } from "@root/shared/components/MultiLineText";
import type {
  Employment,
  WorkRequirement,
  ShopCondition,
} from "@clientFrontend/features/publication/components/WorkRequirementList/type";
import * as styles from "./styles.css";

type Props = {
  employment: Employment;
  workRequirement?: WorkRequirement;
  site: "biyo" | "kaigo";
};

type FixedOvertimePayParams = {
  fixedOvertimePayFlag: boolean;
  fixedOvertimePayAmount?: number;
  fixedOvertimePayWorkingHours?: number;
  salaryType?: string | null;
};

export const WorkRequirementItem: FC<Props> = ({ employment, workRequirement, site }) => {
  if (!workRequirement) {
    return <h3 className={styles.tableTitle[site]}>{employment.name}</h3>;
  }

  const fixedOvertimePayText = ({
    fixedOvertimePayFlag,
    fixedOvertimePayAmount,
    fixedOvertimePayWorkingHours,
    salaryType,
  }: FixedOvertimePayParams): string => {
    if (fixedOvertimePayFlag) {
      const typeText = salaryType === "年収" ? "月" : "";
      return `あり ${typeText}${fixedOvertimePayAmount}円～（${fixedOvertimePayWorkingHours}時間分）`;
    }
    return "なし";
  };

  const formatAmountRange = (min?: number | null, max?: number | null): string => {
    if (min && max) {
      return min === max ? `${min}円` : `${min}円 〜 ${max}円`;
    }
    if (min && !max) return `${min}円〜`;
    if (!min && max) return `〜${max}円`;
    return ""; // 金額なし
  };

  const renderSalaryText = (
    salaryType: ShopCondition["salaryType"],
    salaryMin: number | null,
    salaryMax: number | null,
  ) => {
    const amountText = formatAmountRange(salaryMin, salaryMax);

    // 給与形態が未選択でも給与が入力されている場合
    if (!salaryType && amountText) {
      return (
        <>
          給与形態
          <br />
          給与 {amountText}
        </>
      );
    }

    // 給与形態が選択されている場合
    if (salaryType) {
      if (salaryType !== "完全歩合") {
        return (
          <>
            給与形態 {salaryType}
            <br />
            給与 {amountText}
          </>
        );
      }

      // 完全歩合の場合
      if (amountText) {
        return (
          <>
            給与形態 {salaryType}
            <br />
            給与 {amountText}
          </>
        );
      }
      return <>給与形態 {salaryType}</>;
    }

    return null;
  };

  const salaryText = (
    salaryType: ShopCondition["salaryType"],
    salaryMin: number | null,
    salaryMax: number | null,
  ) => {
    return renderSalaryText(salaryType, salaryMin, salaryMax);
  };

  const trialSalaryText = (
    trialSalaryType: ShopCondition["salaryType"],
    trialSalaryMin: number | null,
    trialSalaryMax: number | null,
  ) => {
    return renderSalaryText(trialSalaryType, trialSalaryMin, trialSalaryMax);
  };

  const trialPeriodText = (trialPeriodMin?: number, trialPeriodMax?: number) => {
    if (!trialPeriodMin && !trialPeriodMax) {
      return "あり";
    }
    if (trialPeriodMin === trialPeriodMax) {
      return `${trialPeriodMin}ヶ月`;
    }
    if (trialPeriodMin && !trialPeriodMax) {
      return `${trialPeriodMin}ヶ月〜`;
    }
    if (!trialPeriodMin && trialPeriodMax) {
      return `〜${trialPeriodMax}ヶ月`;
    }
    return `${trialPeriodMin}ヶ月〜${trialPeriodMax}ヶ月`;
  };

  return (
    <>
      <h3 className={styles.tableTitle[site]}>{employment.name}</h3>
      {workRequirement.shopConditions.map((shopCondition) => (
        <Table key={shopCondition.shopName} className={styles.table}>
          <DetailTableRow header="応募店舗">{shopCondition.shopName}</DetailTableRow>
          <DetailTableRow header="給与">
            {salaryText(shopCondition.salaryType, shopCondition.salaryMin, shopCondition.salaryMax)}
          </DetailTableRow>
          {/* 業務委託以外の場合に表示 */}
          {employment.no !== 3 && (
            <DetailTableRow header="固定残業代">
              {fixedOvertimePayText({
                fixedOvertimePayFlag: shopCondition.fixedOvertimePayFlag,
                fixedOvertimePayAmount: shopCondition.fixedOvertimePayAmount,
                fixedOvertimePayWorkingHours: shopCondition.fixedOvertimePayWorkingHours,
                salaryType: shopCondition.salaryType,
              })}
            </DetailTableRow>
          )}
          <DetailTableRow header="試用期間">
            {shopCondition.trialFlag
              ? trialPeriodText(shopCondition.trialPeriodMin, shopCondition.trialPeriodMax)
              : "なし"}
          </DetailTableRow>
          {shopCondition.trialFlag && (
            <DetailTableRow header="試用期間の給与">
              {trialSalaryText(
                shopCondition.trialSalaryType,
                shopCondition.trialSalaryMin,
                shopCondition.trialSalaryMax,
              )}
            </DetailTableRow>
          )}
          <DetailTableRow header="給与の備考">
            {shopCondition.salaryText && <MultiLineText text={shopCondition.salaryText} />}
          </DetailTableRow>
          <DetailTableRow header="勤務時間">
            {shopCondition.workTimes
              .map((workTime) => `${workTime.workStartTime}〜${workTime.workEndTime}`)
              .join(", ")}
          </DetailTableRow>
          <DetailTableRow header="勤務時間の備考">
            {shopCondition.workTimeText && <MultiLineText text={shopCondition.workTimeText} />}
          </DetailTableRow>
        </Table>
      ))}
      <Table>
        <DetailTableRow header="勤務日数">{workRequirement.workingDays.join("・")}</DetailTableRow>
        <DetailTableRow header="勤務体制">{workRequirement.workstyles.join("・")}</DetailTableRow>
        <DetailTableRow header="休日">{workRequirement.holidays.join("・")}</DetailTableRow>
        <DetailTableRow header="休日の詳細">
          {workRequirement.holidayText && <MultiLineText text={workRequirement.holidayText} />}
        </DetailTableRow>
        <DetailTableRow header="必要資格">
          {workRequirement.necessaryUserCredentials.join("・")}
        </DetailTableRow>
        <DetailTableRow header="必要経験">
          {workRequirement.necessaryExperiences.join("・")}
        </DetailTableRow>
        <DetailTableRow header="特徴">{workRequirement.features.join("・")}</DetailTableRow>
        {workRequirement.welfareDetail ? (
          <DetailTableRow header="福利厚生">
            {workRequirement.welfaresExcludeWithDetail.join("・") && (
              <MultiLineText text={workRequirement.welfaresExcludeWithDetail.join("・")} />
            )}
            {workRequirement.welfareDetail.bonusCheckedAndWithDetail && (
              <>
                <p className={styles.welfareDetailTitle}>ボーナス・賞与あり</p>
                {workRequirement.welfareDetail.bonusNoPaymentPastFlag ? (
                  <MultiLineText text={workRequirement.welfareDetail.bonusNoPaymentPastText} />
                ) : (
                  <>
                    ・{workRequirement.welfareDetail.bonusPaymentYear}年度支給実績（
                    {workRequirement.welfareDetail.bonusPaymentCount}回支給）
                    <p className={styles.welfareDetailText}>
                      {workRequirement.welfareDetail.bonusPaymentTotalAmount}（総額）
                    </p>
                    ・支給条件
                    <p className={styles.welfareDetailText}>
                      {workRequirement.welfareDetail.bonusPaymentConditions}
                    </p>
                  </>
                )}
              </>
            )}
            {workRequirement.welfareDetail.transportationProvisionCheckedAndWithDetail && (
              <>
                <p className={styles.welfareDetailTitle}>交通費支給</p>
                {workRequirement.welfareDetail.transportationProvisionFullPaymentFlag ? (
                  <p className={styles.welfareDetailText}>・全額支給</p>
                ) : (
                  workRequirement.welfareDetail.transportationProvisionPaymentAmountMax && (
                    <>
                      ・上限額
                      <p className={styles.welfareDetailText}>
                        {workRequirement.welfareDetail.transportationProvisionPaymentAmountMax}円
                      </p>
                    </>
                  )
                )}
                {workRequirement.welfareDetail.transportationProvisionPaymentConditions && (
                  <>
                    ・支給条件
                    <p className={styles.welfareDetailText}>
                      {workRequirement.welfareDetail.transportationProvisionPaymentConditions}
                    </p>
                  </>
                )}
              </>
            )}
            {workRequirement.welfareDetail.uniformCheckedAndWithDetail && (
              <>
                <p className={styles.welfareDetailTitle}>制服あり</p>
                {workRequirement.welfareDetail.uniformProvidingConditions && (
                  <>
                    ・支給条件
                    <p className={styles.welfareDetailText}>
                      {workRequirement.welfareDetail.uniformProvidingConditions}
                    </p>
                  </>
                )}
              </>
            )}
          </DetailTableRow>
        ) : (
          <DetailTableRow header="福利厚生">{workRequirement.welfares.join("・")}</DetailTableRow>
        )}
        <DetailTableRow header="福利厚生の詳細">
          {workRequirement.welfareText && <MultiLineText text={workRequirement.welfareText} />}
        </DetailTableRow>
        <DetailTableRow header="仕事内容">
          {workRequirement.workDescriptions.join("・")}
        </DetailTableRow>
        <DetailTableRow header="仕事内容の詳細">
          {workRequirement.workDescriptionText && (
            <MultiLineText text={workRequirement.workDescriptionText} />
          )}
        </DetailTableRow>
      </Table>
    </>
  );
};
